import { useState } from "react";
import type { Image } from "../../types/common/image";
import { useAppDispatch } from "../../redux/hooks";
import { createArticle } from "../../redux/slices/articleSlice";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorHandler";
import RichTextEditor from "../common/ckeditor/RichTextEditor";
import buttonLoadingAnimation from "../../animations/buttonLoading.json";
import Lottie from "lottie-react";
import { useFileUpload } from "../../hooks/useFileUpload";
import { API } from "../../configs/endpoints";

type Props = {
  onBack: () => void;
};

export const CreateArticle = ({ onBack }: Props) => {
  const dispatch = useAppDispatch();

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [thumbnail, setThumbnail] = useState<Image | null>(null);
  const [images, setImages] = useState<Image[]>([]);
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDesc, setSeoDesc] = useState("");
  const [seoKeywords, setSeoKeywords] = useState("");

  const { upload, isUploading: uploading } = useFileUpload<Image>({
    endpoint: API.article.upload,
    kind: "image",
  });
  const handleUploadThumbnail = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0] ?? null;
    const result = await upload(file);
    e.target.value = "";
    if (result.ok && result.data) {
      setThumbnail(result.data);
    }
  };

  const handleEditorImageUpload = (image: Image) => {
    setImages((prev) => [...prev, image]);
  };

  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Vui lòng nhập tiêu đề");
      return;
    }
    if (!excerpt.trim()) {
      toast.error("Vui lòng nhập mô tả ngắn");
      return;
    }
    if (!content.trim()) {
      toast.error("Vui lòng nhập nội dung");
      return;
    }
    if (!thumbnail) {
      toast.error("Vui lòng upload ảnh đại diện");
      return;
    }

    setLoading(true);
    try {
      const payload: Parameters<typeof createArticle>[0] = {
        title,
        excerpt,
        content,
        thumbnail,
        images,
        isPublished,
      };
      if (seoTitle || seoDesc || seoKeywords) {
        payload.seo = {
          title: seoTitle,
          description: seoDesc,
          keywords: seoKeywords
            .split(",")
            .map((k) => k.trim())
            .filter(Boolean),
        };
      }
      await dispatch(createArticle(payload)).unwrap();
      toast.success("Tạo bài viết thành công");
      onBack();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Thêm bài viết mới</h1>
        <button
          type="button"
          onClick={onBack}
          className="p-2 text-sm bg-gray-500 rounded-lg text-white cursor-pointer hover:bg-gray-600 transition-colors duration-300"
        >
          Quay lại
        </button>
      </div>
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl border border-gray-100 p-5 space-y-4"
      >
        {/* Title */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Tiêu đề <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 placeholder:text-sm placeholder:text-gray-400"
            placeholder="Nhập tiêu đề bài viết"
          />
        </div>

        {/* Excerpt */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Mô tả ngắn <span className="text-red-500">*</span>
          </label>
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            rows={3}
            className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 placeholder:text-sm placeholder:text-gray-400 resize-none"
            placeholder="Nhập mô tả ngắn cho bài viết"
          />
        </div>

        {/* Thumbnail */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Ảnh đại diện <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleUploadThumbnail}
            className="text-sm text-gray-500"
          />
          {uploading && (
            <p className="text-sm text-blue-500">Đang upload...</p>
          )}
          {thumbnail && (
            <img
              src={thumbnail.url}
              alt="Thumbnail"
              className="h-32 w-auto object-cover rounded-lg border border-gray-200 mt-2"
            />
          )}
        </div>

        {/* Content (CKEditor) */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Nội dung <span className="text-red-500">*</span>
          </label>
          <RichTextEditor
            value={content}
            onChange={setContent}
            onUpload={handleEditorImageUpload}
          />
        </div>

        {/* Publish status */}
        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">
            Trạng thái
          </label>
          <select
            value={String(isPublished)}
            onChange={(e) => setIsPublished(e.target.value === "true")}
            className="p-2 border border-gray-200 rounded-xl cursor-pointer text-sm text-gray-700 focus:outline-none focus:border-blue-500 transition-colors duration-300"
          >
            <option value="false">Bản nháp</option>
            <option value="true">Xuất bản ngay</option>
          </select>
        </div>

        {/* SEO Section */}
        <div className="border-t border-gray-200 pt-4 space-y-3">
          <p className="text-sm font-semibold text-gray-700">
            SEO (Tùy chọn)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-xs text-gray-500">SEO Title</label>
              <input
                type="text"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 text-sm placeholder:text-gray-400"
                placeholder="Tiêu đề SEO"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-xs text-gray-500">Keywords</label>
              <input
                type="text"
                value={seoKeywords}
                onChange={(e) => setSeoKeywords(e.target.value)}
                className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 text-sm placeholder:text-gray-400"
                placeholder="từ khóa 1, từ khóa 2, ..."
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="block text-xs text-gray-500">
              SEO Description
            </label>
            <textarea
              value={seoDesc}
              onChange={(e) => setSeoDesc(e.target.value)}
              rows={2}
              className="p-2 border border-gray-200 rounded-xl w-full focus:outline-none focus:border-blue-500 transition-colors duration-300 text-sm placeholder:text-gray-400 resize-none"
              placeholder="Mô tả SEO"
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end pt-2">
          {loading ? (
            <Lottie
              animationData={buttonLoadingAnimation}
              loop={true}
              className="h-10 w-20"
            />
          ) : (
            <button
              type="submit"
              className="p-2 px-6 text-sm text-white bg-blue-500 rounded-lg cursor-pointer hover:bg-blue-600 transition-colors duration-300"
            >
              Tạo bài viết
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
